import { sanitizeHTML } from '../../utils/safe-html';
import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl, usePermissions } from '../../permissions/access';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useContext } from 'react';
import { createPost, fetchAllTags, fetchPostCategory, fetchPostCreator, updateCrawledPost, updatePost } from "../../api/community";
import { Descriptions, Input, Select, Space, Button, message, DatePicker, Tag, Typography, Spin, Avatar } from 'antd'
import RichTextEditor from "../../components/editor";
import { CloudStorageImageUploader } from "../../components/upload";
import { UserInfoProviderContext } from "../../router";
import dayjs from "dayjs";
import { conversionUtcDate } from "../../utils/comm";
import '../../style/community.css';
import VoteItemsEditView from "../../components/community/vote_items_edit_view";
import { customUploaderBtn } from "../../components/comm/custom_uploader_btn";
import { useDebouncedCallback } from "use-debounce";
import { UserOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { updateAIArticle } from "../../api/ai";

export const H5Options = [
    {
        key: '1',
        label: 'Allow to login for vote in H5',
        value: 'H5GuideLoginVote',
    },
    {
        key: '2',
        label: 'Allow to login for vote in H5 Top Position',
        value: 'H5GuideLoginTopVote'
    },
    {
        key: '3',
        label: 'Allow to login for long article in H5 ',
        value: 'H5GuideLoginFullArticle'
    }
]

const CommunityDetailScreen = () => {
    const { can } = usePermissions();
    const navigateTo = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [data, setData] = useState({});
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(false);

    const [topics, setTopics] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const [topicTag, setTopicTag] = useState(null);
    const [contentTags, setContentTags] = useState(null);
    const defaultData = location.state && location.state.detail ? JSON.parse(location.state.detail) : { categoryId: '2' };

    const [urlSearchParams] = useSearchParams();
    const isPublishMode = urlSearchParams.get('mode') === 'publish';
    const userInfo = useContext(UserInfoProviderContext);

    useEffect(() => {
        setData({
            ...defaultData,
            events: Array.from(defaultData.events ?? []).map(item => item.eventType).join(','),
        });
        if (defaultData.id) {
            let tTag = Array.from(defaultData.tags).find(item => (item.tag.isTopic === true));
            if (tTag) {
                setTopicTag(tTag.tag.name);
            }
            let cTags = Array.from(defaultData.tags).filter(item => (item.tag.isTopic !== true));
            setContentTags(cTags.map(item => item.tag.name));

            if (defaultData.user) {
                setUsers([
                    {
                        avatar: (defaultData.user.picture ? defaultData.user.picture : defaultData.user.avatar),
                        email: defaultData.user.email,
                        label: defaultData.user.name ? defaultData.user.name : defaultData.user.email,
                        value: defaultData.user.userId,
                    }
                ])
            }
        } else {
            handleUserSearch();
        }

        fetchPostCategory().then(res => {
            if (res.list) {
                setCategories(res.list.map(item => ({ key: item.id, label: item.name, value: item.id })))
            }
        }).catch(showRequestError)
    }, [])

    const onFormChange = (tag, value, extra) => {
        let newData = { ...data };
        if (tag.includes('reward')) {
            let reward = { ...(data.reward ?? {}) }
            if (tag.includes('count')) {
                reward['count'] = value;
            } else if (tag.includes('endAt')) {
                reward['endAt'] = conversionUtcDate(value, 'UTC');
            } else if (tag.includes('desc')) {
                reward['description'] = value;
            } else if (tag.includes('tip')) {
                reward['tip'] = value;
            } else if (tag.includes('rule')) {
                reward['rule'] = value;
            } else if (tag.includes('symbol')) {
                reward['symbol'] = value;
            }
            newData['reward'] = reward;
        } else if (tag.includes('vote')) {
            let vote = { ...(data.vote ?? {}) }
            if (tag.includes('items')) {
                vote['options'] = [...value];
            } else if (tag.includes('desc')) {
                vote['description'] = value;
            } else if (tag.includes('endAt')) {
                vote['endAt'] = conversionUtcDate(value, 'UTC');
            }
            if (!vote.id || parseInt(vote.id) <= 0) {
                delete vote['id'];
            }
            newData['vote'] = vote;
        } else {
            if (tag === 'content') {
                let allTags = contentTags ? [...contentTags] : [];
                if (String(value).length < String(newData.content ?? '').length) {
                    let div = document.createElement('div');
                    div.innerHTML = sanitizeHTML(value);
                    let aLabels = div.querySelectorAll('a');
                    let sets = Array.from(aLabels.entries()).map(item => item[1].innerText).filter(item => item);
                    allTags = allTags.filter(item => sets.some(text => {
                        return item.includes(text.replace('#', '')) || item.includes(text.replace('$', ''));
                    }));
                    setContentTags(allTags);
                } else if (extra) {
                    allTags.push(extra);
                    allTags = Array.from(new Set(allTags));
                    setContentTags(allTags);
                }
            }
            newData[tag] = value;
            if (!newData['events']) {
                newData['events'] = ''
            }
        }
        setData(newData);
    }

    const deleteTag = (tag) => {
        let newData = { ...data };

        let content = newData['content'];
        let div = document.createElement('div');
        div.innerHTML = sanitizeHTML(content);
        let aLabels = div.querySelectorAll('a');
        aLabels.forEach(ele => {
            if (tag.includes(ele.innerText.replace('#', '')) || tag.includes(ele.innerText.replace('$', ''))) {
                ele.remove();
            }
        });
        content = div.innerHTML;
        newData['content'] = content;

        let newContentTags = Array.from(contentTags ?? []).filter(item => item !== tag);
        setContentTags(newContentTags);
        setData(newData);
    }

    const onImageUpdate = (files, key = null) => {
        let newData = { ...data };
        if (key) {
            let imgs = (files ?? []);
            newData[key] = imgs[0] ? (imgs[0].remoteUrl ? imgs[0].remoteUrl : imgs[0].url) : '';
        } else {
            newData.resource = (files ?? []).map(item => (item.remoteUrl ? item.remoteUrl : item.url)).join(',');
        }
        setData(newData);
    }

    const toSubmit = () => {
        setLoading(true);

        let tags = [];
        if (contentTags) {
            tags = [...contentTags];
        }
        if (topicTag) {
            tags = [topicTag, ...tags];
        }
        tags = tags.filter(item => item);
        let newData = { ...data };
        delete newData['adminTags'];
        delete newData['tags'];
        delete newData['topic'];
        delete newData['audioResources'];
        if (Array.isArray(newData['events'])) {
            delete newData['events'];
        }

        // 将 proGlobalOrder 和 proTopicOrder 转换为数字类型
        if (newData.proGlobalOrder !== undefined && newData.proGlobalOrder !== '') {
            newData.proGlobalOrder = Number(newData.proGlobalOrder);
        }
        if (newData.proTopicOrder !== undefined && newData.proTopicOrder !== '') {
            newData.proTopicOrder = Number(newData.proTopicOrder);
        }

        let params = { ...newData, tag: tags };
        if (params.id) {
            delete params.status;
            delete params.riskReason;
            updatePost(params.id, params).then(res => {
                if (res) {
                    message.success('updated!');
                    onClose();
                }
            }).catch((error) => {
                showRequestError(error, error?.message);
            }).finally(() => {
                setLoading(false);
            });

            if (defaultData.aiId) {
                updateAIArticle({ id: defaultData.aiId, cover: data.cover }).then(res => { }).catch(showRequestError)
            }
        } else {
            createPost(params).then(res => {
                if (res) {
                    message.success('updated!');
                    onClose();
                }
            }).catch((error) => {
                showRequestError(error, error?.message);
            }).finally(() => {
                setLoading(false);
            });
        }

        if (defaultData?.crawled) {
            updateCrawledPost({ id: defaultData.crawled.id, status: 1 }).then(res => { }).catch(showRequestError)
        }
    }

    const onClose = () => {
        navigateTo(-1);
    }

    const postMediaUploadProps = {
        defaultFileList: useMemo(() => {
            return (data.resource ?? '').split(',').filter(item => item).map(item => ({
                name: (decodeURIComponent(item).split('/').pop() ?? '').split('?')[0],
                url: item,
                percent: 100,
            }));
        }, [data.resource]),
        name: 'community-uploader',
        bucketPath: '/community',
        listType: 'picture-card',
        isMultiple: true,
        needItemRender: true,
        useCardPreview: true,
        showUploadList: true,
        buttonNode: customUploaderBtn('Upload Media'),
        onUpdate: onImageUpdate
    }

    const aiCoverUploadProps = {
        defaultFileList: useMemo(() => {
            return data.cover ? [{
                name: (decodeURIComponent(data.cover).split('/').pop() ?? '').split('?')[0],
                url: data.cover,
                percent: 100,
            }] : [];
        }, [data.cover]),
        name: 'ai-cover-uploader',
        bucketPath: '/community',
        listType: 'picture-card',
        isMultiple: false,
        needItemRender: true,
        useCardPreview: true,
        showUploadList: true,
        buttonNode: customUploaderBtn('Upload Cover'),
        onUpdate: (files) => {
            onImageUpdate(files, 'cover');
        }
    }

    const getTopicTags = useDebouncedCallback(
        (keyword) => {
            let params = {
                'page.num': 1,
                'page.size': 10,
                'name': keyword,
                'level': 1,
                'isTopicTag': true,
                'status': 0,
            }
            setSearchLoading(true);
            fetchAllTags(params).then(res => {
                if (res.list) {
                    let tmp = res.list.map(item => ({
                        createdAt: conversionUtcDate(item.createdAt, null, true),
                        label: item.name,
                        value: item.name,
                    }));
                    setTopics(tmp);
                }
            }).finally(() => setSearchLoading(false)).catch(showRequestError)
        },
        300
    )

    const handleUserSearch = useDebouncedCallback(
        (keyword) => {
            let params = {
                'page.num': 1,
                'page.size': 10,
                'name': keyword,
            }
            setSearchLoading(true);
            fetchPostCreator(params).then(res => {
                if (res.list) {
                    let tmp = res.list.map(item => ({
                        avatar: (item.picture ? item.picture : item.avatar),
                        email: item.email,
                        label: item.name,
                        value: item.userId,
                    }));
                    setUsers(tmp);
                }
            }).finally(() => setSearchLoading(false)).catch(showRequestError)
        },
        300
    )

    return (
        <div style={{ padding: '20px 40px' }}>
            <div className="title">{id ? (id === '0' ? 'Create New Post' : 'Edit Post') : ''}</div>
            <div style={{ height: 30 }} />
            <Descriptions layout="vertical" size='large' bordered column={2} labelStyle={{ width: 250, textAlign: 'center' }}>
                <Descriptions.Item label='Post Category' span={1}>
                    <Select disabled={isPublishMode} value={data.categoryId} style={{ width: '100%' }} options={categories} placeholder='Select category' onChange={value => onFormChange('categoryId', value)} />
                </Descriptions.Item>
                <Descriptions.Item label='Recommend Weight' span={1}>
                    <Input value={data.recommend} type="number" placeholder="article recommend weight" onChange={evt => onFormChange('recommend', evt.target.value)} />
                </Descriptions.Item>
                {
                    data.categoryId == 2 && (
                        <>
                            <Descriptions.Item label='Title' span={1}>
                                <Input value={data.title} placeholder="article title" onChange={evt => onFormChange('title', evt.target.value)} allowClear />
                            </Descriptions.Item>
                            <Descriptions.Item label='Type' span={1}>
                                <Select
                                    value={data.articleType}
                                    style={{ width: '100%' }}
                                    options={[
                                        { key: 0, label: 'Normal', value: 0 },
                                        { key: 1, label: 'Insight', value: 1 },
                                        { key: 2, label: 'Pick', value: 2 },
                                        { key: 3, label: 'Weekly', value: 3 },
                                    ]}
                                    placeholder='Select article type'
                                    onChange={value => onFormChange('articleType', value)}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro Subtitle' span={1}>
                                <Input value={data.proSubtitle} placeholder="pro subtitle" onChange={evt => onFormChange('proSubtitle', evt.target.value)} allowClear />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro Ticker' span={1}>
                                <Input value={data.proTicker} placeholder="pro ticker" onChange={evt => onFormChange('proTicker', evt.target.value)} allowClear />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro Topic' span={1}>
                                <Select
                                    value={data.proTopic}
                                    style={{ width: '100%' }}
                                    options={[
                                        { key: 0, label: '-', value: '' },
                                        { key: 1, label: 'Marco Event', value: 'Marco Event' },
                                        { key: 2, label: 'Morning Wrap', value: 'Morning Wrap' },
                                        { key: 3, label: 'Asian Market', value: 'Asian Market' },
                                        { key: 4, label: 'Crypto Market', value: 'Crypto Market' },
                                        { key: 5, label: 'FX & Commodities', value: 'FX & Commodities' },
                                        { key: 6, label: 'Private Market', value: 'Private Market' },
                                    ]}
                                    placeholder='Select Pro Topic'
                                    onChange={value => onFormChange('proTopic', value)}
                                    allowClear
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro GoAI Conviction' span={1}>
                                <Select
                                    value={data.proGoaiConviction}
                                    style={{ width: '100%' }}
                                    options={[
                                        { key: 1, label: 'GoAI Alpha', value: 'GoAI Alpha' },
                                        { key: 2, label: 'High Conviction', value: 'High Conviction' },
                                        { key: 3, label: 'Medium Conviction', value: 'Medium Conviction' },
                                    ]}
                                    placeholder='Select GoAI Conviction'
                                    onChange={value => onFormChange('proGoaiConviction', value)}
                                    allowClear
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro Global Order' span={1}>
                                <Input value={data.proGlobalOrder} type="number" placeholder="pro global order" onChange={evt => onFormChange('proGlobalOrder', evt.target.value)} allowClear />
                            </Descriptions.Item>
                            <Descriptions.Item label='Pro Topic Order' span={1}>
                                <Input value={data.proTopicOrder} type="number" placeholder="pro topic order" onChange={evt => onFormChange('proTopicOrder', evt.target.value)} allowClear />
                            </Descriptions.Item>
                        </>
                    )
                }
                {
                    data.aiId && (
                        <Descriptions.Item label='AI News Cover' span={2}>
                            <Space align="center">
                                <CloudStorageImageUploader {...aiCoverUploadProps} />
                            </Space>
                        </Descriptions.Item>
                    )
                }

                <Descriptions.Item label='Topic' span={2}>
                    <Select
                        style={{ width: '100%' }}
                        size='large'
                        placeholder='you can input keywords to search topic'
                        defaultActiveFirstOption={false}
                        value={topicTag}
                        suffixIcon={null}
                        filterOption={false}
                        onSearch={(value) => getTopicTags(value)}
                        notFoundContent={searchLoading ? <Spin /> : null}
                        options={topics}
                        optionRender={(option) => {
                            return (
                                <Space>
                                    <div style={{ flex: 1 }}>
                                        <b style={{ wordBreak: 'break-word' }}>{option.data.label}</b>
                                        <div>
                                            <Typography.Text style={{ color: '#999', fontSize: 12 }}>
                                                {option.data.createdAt}
                                            </Typography.Text>
                                        </div>
                                    </div>
                                </Space>
                            )
                        }}
                        onChange={(value) => setTopicTag(value)}
                        allowClear
                        showSearch
                    />
                </Descriptions.Item>

                <Descriptions.Item label='Author' span={2}>
                    <Select
                        style={{ width: '100%' }}
                        value={data.userId}
                        disabled={data.id || !can('post.select-author')}
                        placeholder='search user'
                        defaultActiveFirstOption={false}
                        suffixIcon={null}
                        filterOption={false}
                        onSearch={handleUserSearch}
                        notFoundContent={searchLoading ? <Spin /> : null}
                        options={users}
                        optionRender={(option) => {
                            return (
                                <Space>
                                    {
                                        option.data.avatar ?
                                            <Avatar size={32} src={option.data.avatar} />
                                            :
                                            <Avatar size={32} style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
                                    }
                                    <div>
                                        <b>{option.data.label ? option.data.label : 'anonymous'}</b>
                                        <div>
                                            <Typography.Text style={{ color: '#999', fontSize: 12 }}>{option.data.email ?? option.data.value}</Typography.Text>
                                        </div>
                                    </div>
                                </Space>
                            )
                        }}
                        onChange={value => onFormChange('userId', value)}
                        allowClear
                        showSearch
                    />
                </Descriptions.Item>
                {
                    (data.categoryId && categories.length > 0 && categories.find(item => item.value === data.categoryId).label.toLowerCase() === 'post') &&
                    <Descriptions.Item label='Post Media' span={2}>
                        <Space align="center">
                            <CloudStorageImageUploader {...postMediaUploadProps} />
                        </Space>
                    </Descriptions.Item>
                }
                <Descriptions.Item label='Content' span={2}>
                    <RichTextEditor
                        defaultHeight={700}
                        defaultContent={data.content}
                        contentField='content'
                        needEditTags={true}
                        onUpdate={(field, result, tagName) => onFormChange(field, result, tagName)} />

                    <Space wrap={true} style={{ marginTop: 20 }}>
                        {
                            (contentTags ?? []).filter(item => item).map((item, index) => {
                                return <Tag color="blue" key={index} closable onClose={(evt) => {
                                    evt.preventDefault();
                                    deleteTag(item);
                                }}>{`${item.includes('$') ? item : ('#' + item)}`}</Tag>
                            })
                        }
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label='H5 Action' span={2}>
                    <Select
                        options={H5Options.map(item => {
                            let disabled = false;
                            if (item.value === 'H5GuideLoginFullArticle' && data.categoryId == 1) {
                                disabled = true;
                            }
                            return { ...item, disabled }
                        })}
                        allowClear
                        placeholder='select h5 login action'
                        style={{ width: '100%' }}
                        value={data.events ? data.events : undefined}
                        onChange={(value) => onFormChange('events', value)} />
                </Descriptions.Item>
                <Descriptions.Item label='Votes Config' span={2}>
                    <div className="reward">
                        <Descriptions size="small" column={1} layout="vertical" colon={false} labelStyle={{ marginBottom: 0 }}>
                            <Descriptions.Item label='Vote Desc' span={1}>
                                <Input
                                    style={{ width: 400 }}
                                    placeholder="vote description"
                                    value={data.vote ? data.vote.description : ''}
                                    variant="filled"
                                    onChange={evt => onFormChange('vote_desc', evt.target.value)}
                                    allowClear
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label='Vote EndAt' span={1}>
                                <DatePicker
                                    variant="filled"
                                    showTime
                                    minDate={data.vote && data.vote.createdAt ? dayjs(data.vote.createdAt).add(1, 'day') : dayjs(Date.now()).add(1, 'day')}
                                    defaultValue={(defaultData.vote && defaultData.vote.endAt) ? dayjs(defaultData.vote.endAt) : ''}
                                    format={(value) => `${value ? conversionUtcDate(value) : ''}`}
                                    placeholder="End At"
                                    onChange={(_, date) => onFormChange('vote_endAt', date)}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label='Vote Items' span={1}>
                                <VoteItemsEditView defaultTags={data.vote && data.vote.options ? data.vote.options : []} onUpdate={(value) => onFormChange('vote_items', value)} />
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                </Descriptions.Item>
            </Descriptions>
            <div className="btn-area">
                <PermissionControl permission="adminpost.control.button.fa31a370" variant={(data?.id) ? 'update' : 'create'}><Button loading={loading} type="primary" style={{ width: 200, height: 44 }} onClick={toSubmit}>Submit</Button></PermissionControl>
                <div style={{ width: 24 }} />
                <Button loading={loading} block style={{ width: 200, height: 44 }} onClick={onClose}>Cancel</Button>
            </div>
        </div>
    )
}

export default CommunityDetailScreen;